import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { RACK_COLORS } from "../../../constants"
import { getRackModel } from "../../../modelPaths"
import { CaveOptions } from "../../../RackTypes"
import { MaterialBuilder } from "../../rack/builders/materialBuilder"

export class CaveAccessoryBuilder {
  protected config: CaveOptions
  protected material: MaterialInfo
  protected line: string
  protected foldBackDepth: CaveOptions['foldBackDepth']
  protected height: CaveOptions['height']
  protected isRM: boolean
  protected materialBuilder

  constructor(config: CaveOptions, material: MaterialInfo, materialBuilder: MaterialBuilder) {
    this.config = config
    this.material = material
    this.materialBuilder = materialBuilder
    this.line = config.line
    this.foldBackDepth = config.foldBackDepth
    this.height = config.height
    this.isRM = config.line === 'RM'
  }

  createJCup(left: boolean): PartDefinition {
    const ghostJcupMaterials = this.getGhostJcupMaterials(left)
    
    return {
      mesh        : getRackModel(`${this.config.line}_JCUPS`, this.config.jcup!),
      slots       : {},
      materials   : this.config.jcup === 'ghost-jcup' ? ghostJcupMaterials : {},
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Z', amount: 180 }, { type: 'flip', axis: 'Y' }],
    }
  }

  private getGhostJcupMaterials(left: boolean): MaterialInfo {
    const rightSticker = { 
      sticker: { 
        color  : '#ffffff', 
        config : { maps: { map: 'https://assets.roguefitness.com/w_512,q_auto,f_auto/glb/txt/jcups/ghost_right.jpg' } }, 
      }, 
    }
    
    if (this.config.line === 'RM' && left) {
      return { pin: { material: 'stainless' } }
    }
    if (this.config.line === 'RM' && !left) {
      return { pin: { material: 'stainless' }, ...rightSticker }
    }
    if (!left) {
      return rightSticker
    }
    
    return {}
  }

  createNameplate(): PartDefinition {
    const isRM = this.isRM
    const materials = isRM
      ? { main: { color: RACK_COLORS['Rogue Red'] }, hardware: { material: this.config.hardware } }
      : this.material

    return {
      mesh      : getRackModel(`${this.line}_NAMEPLATE`, 'nameplate'),
      slots     : {},
      anchor    : isRM ? 'left' : '',
      materials : isRM ? this.materialBuilder.createNameplateMaterials() : materials,
      offset    : this.height === '90' ? { y: 53 } : { y: 63 },
    }
  }

  // Creates the back, top crossmember (nameplate, crossmember, or pullup bar)
  createBackCrossmember(): PartDefinition | undefined {
    if (!this.config.back) return undefined

    const line = this.config.line
    const backSelection = this.config.back
    const isNameplate = backSelection === 'nameplate'
    let modelType

    if (isNameplate) {
      modelType = 'NAMEPLATE'
    } else {
      modelType = 'CROSSMEMBERS'
    } 

    const materials = isNameplate
      ? this.materialBuilder.createNameplateMaterials()
      : this.materialBuilder.createBackCrossmemberMaterials()
    
    const part: PartDefinition = {
      mesh        : getRackModel(`${line}_${modelType}`, backSelection),
      anchor      : backSelection.includes('crossmember') ? 'snowman_bottom' : 'left',
      adjustments : backSelection.includes('crossmember')
        ? [{ type: 'rotate', axis: 'Y', amount: 90 }]
        : [],
      slots  : {},
      materials,
      offset : this.height === '90' ? { y: 49 } : { y: 59 },
    }
    
    return part
  }

  createFoldBackPullupBar(leftSide: PartDefinition): void {
    const mainMaterial = { name: 'metal' as const, material: 'mg' as const }
    const materials: MaterialInfo = { main: mainMaterial }

    leftSide.slots['crossmember-top'] = {
      mesh   : getRackModel(`CAVE_${this.line}`, 'pullup'),
      materials,
      slots  : {},
      anchor : 'pin',
      offset : { y: -8, x: 0.5 },
    }
  }

  createSafeties(): PartDefinition {
    let strapOffset = {}

    if (this.foldBackDepth === '21') {
      strapOffset = { z: 1.5 }
    } else if (this.foldBackDepth === '24') {
      strapOffset = { z: 0.3 }
    } else if (this.foldBackDepth === '41') {
      strapOffset = { z: -0.3 }
    }

    return {
      mesh      : getRackModel(`${this.config.line}_SAFETY`, `${this.config.safeties}-${this.config.depth}`),
      slots     : {},
      anchor    : 'main',
      materials : this.material,
      offset    : strapOffset,
    }
  }

  createPlateStorage(left: boolean, pinType: 'short' | 'long' = 'short', yOffset: number = 0): PartDefinition {
    const pins: PartDefinition = {
      mesh   : getRackModel('CAVE_RM_STORAGE', `${pinType} plate storage`),
      slots  : {},
      anchor : 'right',
    }

    const panel: PartDefinition = {
      mesh        : getRackModel('CAVE_RM_STORAGE', 'panel 24 sidewinder'),
      slots       : {},
      anchor      : left ? 'far' : 'near',
      offset      : { x: 2.1, y: yOffset },
      adjustments : left
        ? [{ type: 'rotate', axis: 'Y', amount: 90}, {type: 'flip', axis: 'Z' }, {type: 'flip', axis: 'X' }]
        : [{ type: 'rotate', axis: 'Y', amount: 90}, {type: 'flip', axis: 'Z' }, {type: 'flip', axis: 'X' }],
    }

    panel.slots['weight-storage'] = pins

    return panel
  }
}